import {
  Height as HeightIcon,
  Straighten as StraightenIcon,
  FitnessCenter as FitnessCenterIcon,
} from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import CustomTabs, { TabConfig } from "components/common/CustomTabs";
import { EPath } from "constants/path";
import PatientDetailPageContainer from "pages/components/PatientDetailPageContainer";
import React, { useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router";

import DashboardLayout from "../components/layouts/DashboardLayout";
import BodyCompositionTabContent from "./components/BodyCompositionTabContent";
import { BODY_COMPOSITION_QUERY_KEY } from "./components/BodyCompositionTabContent";
import BodyShapeTabContent from "./components/BodyShapeTabContent";
import { BODY_SHAPE_QUERY_KEY } from "./components/BodyShapeTabContent";
import HeightTabContent from "./components/HeightTabContent";
import { HEIGHT_QUERY_KEY } from "./components/HeightTabContent";

const BodyMeasurementsPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Track active tab to update header meta dynamically and scope refresh
  const [activeTab, setActiveTab] = useState(0);

  const handleBack = () => {
    navigate(EPath.PATIENT_DASHBOARD.replace(":patientId", patientId || ""));
  };

  const handleRefresh = useCallback(() => {
    const baseKey =
      activeTab === 0
        ? HEIGHT_QUERY_KEY
        : activeTab === 1
          ? BODY_SHAPE_QUERY_KEY
          : BODY_COMPOSITION_QUERY_KEY;
    // Invalidate queries for the active tab; mounted observers will refetch
    queryClient.invalidateQueries({
      queryKey: [baseKey, patientId],
      exact: false,
    });
  }, [activeTab, patientId, queryClient]);

  if (!patientId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Patient ID not found</Typography>
      </Box>
    );
  }

  // Define tabs configuration
  const tabs: TabConfig[] = [
    {
      label: "Height",
      content: <HeightTabContent patientId={patientId} />,
    },
    {
      label: "Body Shape",
      content: <BodyShapeTabContent patientId={patientId} />,
    },
    {
      label: "Body Composition",
      content: <BodyCompositionTabContent patientId={patientId} />,
    },
  ];

  const headerMeta = useMemo(() => {
    switch (activeTab) {
      case 0:
        return { title: "Height", icon: <HeightIcon /> };
      case 1:
        return { title: "Body Shape", icon: <StraightenIcon /> };
      case 2:
        return { title: "Body Composition", icon: <FitnessCenterIcon /> };
      default:
        return { title: "Height", icon: <HeightIcon /> };
    }
  }, [activeTab]);

  return (
    <DashboardLayout title="Body Measurements">
      <PatientDetailPageContainer
        breadcrumbs={[
          { label: "Dashboard", href: "/dashboard", order: 0 },
          {
            label: "Patient Management",
            href: EPath.PATIENT_MANAGEMENT,
            order: 1,
          },
          {
            label: "Patient Details",
            href: EPath.PATIENT_DASHBOARD,
            order: 2,
          },
        ]}
        lastBreadcrumbItem={headerMeta.title}
        title={headerMeta.title}
        icon={headerMeta.icon}
        onBack={handleBack}
        onRefresh={handleRefresh}
        containerSx={{
          height: "calc(100vh - 100px)",
        }}
        contentSx={{
          minHeight: 0,
        }}
        titleSx={{
          p: 0,
          m: 0,
        }}
      >
        <Box className="flex-1">
          {/* Tabs */}
          <Box>
            <CustomTabs
              tabs={tabs}
              defaultTab={0}
              distributeEvenly={true}
              onTabChange={setActiveTab}
            />
          </Box>
        </Box>
      </PatientDetailPageContainer>
    </DashboardLayout>
  );
};

export default BodyMeasurementsPage;
